import React, { createContext, useCallback, useContext, useRef } from 'react';
import HOCWithDisplayName from '../utils/HOCWithDisplayName';

type Listener = () => unknown;

const Context = createContext<{
  addUIElementResizeListener: (listener: Listener) => () => void;
  uiElementResize: () => void;
}>({
  addUIElementResizeListener() {
    return () => {};
  },
  uiElementResize() {},
});

export const useUIElementResize = () => {
  const { uiElementResize } = useContext(Context);
  return uiElementResize;
};

export const withUIElementResize = <P extends WithUIElementResizeProps>(
  Component: React.ComponentType<P>,
) => {
  const WrappedComponent = (props: Omit<P, keyof WithUIElementResizeProps>) => {
    const uiElementResize = useUIElementResize();
    return <Component {...(props as P)} uiElementResize={uiElementResize} />;
  };

  return HOCWithDisplayName('withUIElementResize')(WrappedComponent);
};

export interface WithUIElementResizeProps {
  uiElementResize: () => void;
}

export const useUIElementResizeListener = () => {
  const { addUIElementResizeListener } = useContext(Context);
  return addUIElementResizeListener;
};

interface UIElementResizeContextProps {
  children: React.ReactNode;
}

const UIElementResizeContext = ({ children }: UIElementResizeContextProps) => {
  const listeners = useRef<Listener[]>([]);

  const addUIElementResizeListener = useCallback((listener: Listener) => {
    listeners.current.push(listener);
    return () => {
      const index = listeners.current.indexOf(listener);
      if (index !== -1) {
        listeners.current.splice(index, 1);
      }
    };
  }, []);

  const uiElementResize = useCallback(
    () => listeners.current.forEach((listener) => listener()),
    [],
  );

  return (
    <Context.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values -- Fix next time editing the file
      value={{
        addUIElementResizeListener,
        uiElementResize,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default UIElementResizeContext;
